import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const FooterSection = styled.footer`
  background: var(--color-card);
  padding: 3rem 2rem;
  text-align: center;
  border-top: 4px solid var(--color-primary);
  position: relative;
  overflow: hidden;
  margin-top: 0;
  box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '🐾';
    position: absolute;
    font-size: 15rem;
    opacity: 0.05;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-15deg);
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled.h3`
  font-family: var(--font-display);
  font-size: 2rem;
  color: var(--color-primary);
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  
  span {
    color: var(--color-accent);
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
`;

const FooterLinkBase = `
  color: var(--color-text-light);
  font-weight: 700;
  font-family: var(--font-alt);
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  text-decoration: none;

  &:hover {
    background: var(--color-primary);
    color: #000;
    transform: translateY(-2px);
  }
`;

const FooterLink = styled.a`
  ${FooterLinkBase}
`;

const FooterLinkRouter = styled(Link)`
  ${FooterLinkBase}
`;

const Disclaimer = styled.p`
  color: #2C2C2C;
  font-size: 1rem;
  margin: 2rem auto;
  max-width: 800px;
  line-height: 1.6;
  font-family: var(--font-body);
  font-weight: 700;
  background: rgba(255, 229, 92, 0.9);
  padding: 1.5rem;
  border-radius: 20px;
  border: 3px solid var(--color-primary);
  position: relative;
  z-index: 1;
`;

const Copyright = styled.p`
  color: var(--color-text-light);
  font-size: 0.9rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 215, 0, 0.3);
  font-weight: 600;

  a {
    color: var(--color-primary);
    font-weight: 700;
    text-decoration: underline;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-bottom: 2rem;
  position: relative;
  z-index: 1;
`;

const SocialLink = styled.a`
  font-size: 2rem;
  color: var(--color-text-light);
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.1);

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }

  &:hover {
    transform: translateY(-5px) scale(1.1);
    background: var(--color-primary);
    color: #000;
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.4);
  }
`;

const LegalLinks = styled.div`
  font-size: 0.75rem;
  color: var(--color-text-light);
  margin-top: 1rem;

  a {
    color: var(--color-text-light);
    text-decoration: underline;
    margin: 0 0.5rem;
    transition: color 0.3s ease;

    &:hover {
      color: var(--color-primary);
    }
  }
`;

const Footer: React.FC = () => {
  return (
    <FooterSection>
      <FooterContent>
        <Logo>
          Pepe's Dog <span>Army</span>
        </Logo>

        <SocialLinks>
          <SocialLink href="https://x.com/pepesdog_en" target="_blank" rel="noopener noreferrer" title="Follow us on X">
            𝕏
          </SocialLink>
          <SocialLink href="https://t.me/+oC_dPpWk4_IwMzM0" target="_blank" rel="noopener noreferrer" title="Join our Telegram">
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="21" viewBox="0 0 25 21" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M21.8964 0.190419C22.1963 0.0641831 22.5246 0.0206453 22.847 0.0643364C23.1695 0.108027 23.4744 0.237351 23.7299 0.438845C23.9854 0.640339 24.1823 0.906639 24.2999 1.21003C24.4176 1.51341 24.4518 1.8428 24.399 2.16389L21.6463 18.8607C21.3793 20.4713 19.6122 21.3949 18.1351 20.5927C16.8996 19.9215 15.0645 18.8874 13.4138 17.8085C12.5885 17.2684 10.0604 15.5389 10.3711 14.3082C10.6381 13.2559 14.886 9.30165 17.3134 6.95072C18.2662 6.0271 17.8317 5.49428 16.7066 6.34387C13.9127 8.45328 9.42683 11.6611 7.94369 12.5641C6.63532 13.3603 5.95322 13.4962 5.13762 13.3603C3.64962 13.1127 2.26965 12.7291 1.14334 12.2619C-0.378642 11.6307 -0.304606 9.53833 1.14212 8.92905L21.8964 0.190419Z" fill="currentColor"/>
            </svg>
          </SocialLink>
          <SocialLink href="https://coinmarketcap.com/currencies/pepes-dog/" target="_blank" rel="noopener noreferrer" title="View on CoinMarketCap">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M20.5899 14.3418C20.1713 14.6061 19.6785 14.6391 19.3038 14.4277C18.828 14.1591 18.5661 13.5309 18.5661 12.6544V10.0363C18.5661 8.77185 18.0662 7.87225 17.2303 7.62918C15.8144 7.21666 14.7486 8.95074 14.3479 9.60066L11.8497 13.6512V8.70058C11.8214 7.56121 11.4518 6.87966 10.749 6.67435C10.2846 6.53842 9.5889 6.59317 8.91348 7.62682L3.31621 16.6139C2.56687 15.1914 2.17624 13.6075 2.17825 11.9998C2.17825 6.58562 6.51674 2.18246 11.8497 2.18246C17.1827 2.18246 21.5207 6.58703 21.5207 11.9998C21.5207 12.0092 21.5231 12.0172 21.5235 12.0257C21.524 12.0342 21.5235 12.0437 21.5235 12.0531C21.574 13.1014 21.2347 13.9354 20.5914 14.3418H20.5899ZM23.6696 12.0007V11.9738V11.9469C23.639 5.35279 18.349 0 11.8492 0C5.33206 0 0.0292969 5.383 0.0292969 12.0002C0.0292969 18.6175 5.33206 24 11.8497 24C14.8391 24.0005 17.715 22.8553 19.8857 20.7999C20.0955 20.6015 20.219 20.3287 20.2296 20.0401C20.2403 19.7516 20.1373 19.4704 19.9428 19.257C19.7533 19.0482 19.4885 18.9231 19.2068 18.9094C18.9251 18.8957 18.6495 18.9944 18.4405 19.1839L18.4245 19.1985C16.6486 20.8804 14.2956 21.8176 11.8497 21.8171C8.99419 21.8171 6.42518 20.5531 4.65334 18.549L9.70076 10.4455V14.1818C9.70076 15.9753 10.3969 16.5568 10.9803 16.7262C11.5637 16.8957 12.4572 16.78 13.3945 15.2584L16.1712 10.7575C16.2604 10.6121 16.3421 10.487 16.4171 10.3799V12.6558C16.4171 14.3337 17.0892 15.6765 18.2607 16.3373C19.3165 16.933 20.6442 16.8792 21.726 16.1957C23.0367 15.366 23.7433 13.84 23.6696 12.0007Z" fill="currentColor"/>
            </svg>
          </SocialLink>
        </SocialLinks>

        <FooterLinks>
          <FooterLink href="/#mission">Mission</FooterLink>
          <FooterLinkRouter to="/governance">Governance</FooterLinkRouter>
          <FooterLinkRouter to="/ens">Get ENS</FooterLinkRouter>
          <FooterLink href="mailto:hello@pepes.dog">Contact</FooterLink>
        </FooterLinks>
        
        <Disclaimer>
          ⚠️ Pepe's Dog Army is a community of diamond-pawed holders and Pepe's Dog enthusiasts.
          We are not financial advisors - we're just dogs who love memes! Always DYOR
          (Do Your Own Research) before aping in. Remember: crypto is risky, you could
          lose all your treats... I mean, capital! Woof responsibly! 🐶
        </Disclaimer>
        
        <Copyright>
          © 2025 Pepe's Dog Army. All paws reserved. |
          Made with 💙 by the pack for the pack |
          <a href="https://zeuscoin.vip" target="_blank" rel="noopener noreferrer"> ZEUS Official</a>
        </Copyright>

        <LegalLinks>
          <FooterLinkRouter to="/terms">Terms & Conditions</FooterLinkRouter>
          |
          <FooterLinkRouter to="/privacy">Privacy Policy</FooterLinkRouter>
        </LegalLinks>
      </FooterContent>
    </FooterSection>
  );
};

export default Footer;